import { Cvt } from "../Shared"
import {
  LoanCaseDTO,
  LoanCaseStatusDTO,
  OnlineInfoDTO,
  ProductInstanceDTO,
  RealEstateAddressDTO,
  RealEstateCollateralDTO,
  ValueList,
} from "@smartx/mikro-vt-client-api"
import { Icon } from "@george-labs.com/design-system"
import TranslatedText from "../components/TranslatedText/TranslatedText"
import { SuccessCaseTypeList } from "../utils/constants"
import { getCvtValue, isBianco, isBiancoCase, isPalCase } from "./helpers"
import { VALUE_LIST_ENUM } from "./setCodeTableValues"
import { ICON } from "@george-labs.com/george-assets/icons"

export const getIcon = (loanCaseStatus: LoanCaseStatusDTO) => {
  const onlineInfo: OnlineInfoDTO = loanCaseStatus.onlineInfo

  if (onlineInfo?.onlineRectification) {
    return (
      <Icon
        className="mr-1"
        assetIcon={ICON.ERROR_CIRCLE}
        color={Icon.COLOR.UI_RED}
      />
    )
  }

  return SuccessCaseTypeList.includes(loanCaseStatus.status) ? (
    <Icon
      className="mr-1"
      assetIcon={ICON.CHECKMARK_CIRCLE}
      color={Icon.COLOR.UI_GREEN}
    />
  ) : (
    <Icon className="mr-1" assetIcon={ICON.CLOCK} color={Icon.COLOR.UI_GRAY} />
  )
}

export const productCodes = {
  EHFH: "FPCFHEH",
  FH: "FPCFH",
  EH: "FPCEH",
  EXIT: "PBEXIT",
  SZKFH: "SZKFH",
  SZKEH: "SZKEH",
  ASZKFH: "ASZKFH",
  BSZKEH: "BSZKEH",
  PALFH: "PALFH",
  BIANCO: "BIANCO",
}

// Termék szűrés lista termékkódok alapján
export const ProductCategoryTypeCodes = {
  pb: [productCodes.FH, productCodes.EH, productCodes.EHFH, productCodes.EXIT],
  szk: [
    productCodes.SZKEH,
    productCodes.SZKFH,
    productCodes.ASZKFH,
    productCodes.BSZKEH,
  ],
  klikk: [productCodes.PALFH],
  bianco: [productCodes.BIANCO],
}

export const getStatusName = (loanCaseStatus: LoanCaseStatusDTO) => {
  const onlineInfo: OnlineInfoDTO = loanCaseStatus.onlineInfo

  const statusName = (
    <Cvt list="MIKRO_ONLINE_STATE_LIST" code={loanCaseStatus.status}></Cvt>
  )

  if (onlineInfo?.onlineRectification) {
    return <TranslatedText text="rectification.statusName" />
  }

  return loanCaseStatus.suspended ? (
    <>
      {statusName}
      <TranslatedText text="case.status.postfix.SUSPENDED" />
    </>
  ) : (
    statusName
  )
}

export const getProductName = (item: LoanCaseDTO) => {
  if (isPalCase(item) || isBiancoCase(item)) {
    return item.productInstances?.find((el) => !!el)?.subProductName
  }

  return <Cvt list={VALUE_LIST_ENUM.ProductList} code={item.productCode}></Cvt>
}

export const getCollateralAddress = (
  collateral: RealEstateCollateralDTO,
  valueLists: ValueList[],
  locationText: string,
) => {
  const address = collateral?.realEstateAddresses?.find(
    (el) => el.addressType === "term_cim",
  )

  if (!address) {
    return ""
  }

  const hideLocation = false

  const formattedAddressPart1 = _getCollateralAddressData(
    address,
    ["postalCode", "city"],
    valueLists,
  )

  const formattedAddressPart2 = _getCollateralAddressData(
    address,
    [
      "street",
      "streetType",
      "houseNumber",
      "houseNumLetter",
      "building",
      "staircase",
      "floor",
      "flatNumber",
    ],
    valueLists,
  )

  const formattedLocation = address.location
    ? locationText + " " + address.location
    : ""

  return [
    formattedAddressPart1,
    formattedAddressPart2 || (hideLocation ? "" : formattedLocation),
  ]
    .filter((el) => !!el)
    .join(" ")
}

const _getCollateralAddressData = <K extends keyof RealEstateAddressDTO>(
  address: RealEstateAddressDTO,
  keys: K[],
  valueLists: ValueList[],
): string => {
  return (
    keys
      .map((key) => {
        const value = address[key] || ""

        if (key === "streetType") {
          return getCvtValue(valueLists, VALUE_LIST_ENUM.SapStreetType, value)
        }

        if (key === "floor") {
          return getCvtValue(valueLists, VALUE_LIST_ENUM.SapFloor, value)
        }

        return value
      })
      .filter((el) => !!el)
      .join(" ") || ""
  )
}

export const getProductAccordionTitle = (
  product: ProductInstanceDTO,
): string => {
  let productCode: string = product.productCode

  if (isBianco(product?.productCode)) {
    const biancoProductType = product.biancoProductType

    switch (biancoProductType) {
      case "EGYEB":
        return ""

      case "EH":
        productCode = productCodes.EH
        break

      case "FH":
        productCode = productCodes.FH
        break
    }
  }

  return "case.fields.productInstance." + productCode
}

export const getLoanPurposeBiancoCvtList = (
  product: ProductInstanceDTO,
): string[] => {
  const biancoProduct = product.biancoProduct
  const biancoProductType = product.biancoProductType

  switch (biancoProduct) {
    case "JELZALOG": {
      return [VALUE_LIST_ENUM.LoanPurposeListBiancoJelzalog]
    }

    case "LOMBARD_ÉP":
    case "LOMBARD_KP": {
      if (biancoProductType === "EGYEB") {
        return [
          VALUE_LIST_ENUM.LoanPurposeListBiancoLombardFh,
          VALUE_LIST_ENUM.LoanPurposeListBiancoLombardEh,
        ]
      }

      return biancoProductType === "FH"
        ? [VALUE_LIST_ENUM.LoanPurposeListBiancoLombardFh]
        : [VALUE_LIST_ENUM.LoanPurposeListBiancoLombardEh]
    }

    case "EGYEB": {
      return [VALUE_LIST_ENUM.LoanPurposeListBiancoAltalanos]
    }

    case "BANKGARANCIA": {
      return [VALUE_LIST_ENUM.BankGuaranteeType]
    }

    default:
      return []
  }
}
